import React, {
  createContext, useContext, useMemo,
} from 'react';
import {
  serverTimestamp, getDocsFromServer, query, where, limit as limitTo,
} from 'firebase/firestore';
import { arrayRemove, writeBatch, writeQueue } from './writes';
import { queuedStudentMentions } from './queuedHistory';
import { col, docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { coreSync } from '../lib/core';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import {
  normaliseStudent, normaliseFamily, normaliseFamilyContact, studentStatus,
  studentDurationMin, planStudentDeletion, planTagDetachment, unansweredReadMessage,
} from '../domain/people';
import { freeId } from '../domain/freeId';
import { tagDetachmentDocument } from '../domain/tags';
import { useCollectionWindow } from './useCollectionWindow';

// Students.
//
// A stored student carries an integer `rateMinor` and a pricing *mode*: MMS
// supports four (per-lesson, monthly, package, free), and a single flat rate
// field can only express one of them. Decimals are a thing the *forms* deal in;
// the only place one is allowed to become minor units is the write path below.

const StudentsContext = createContext(null);
const PAGE_LIMIT = 500;

export function normalizeStudent(raw) {
  return {
    ...raw,
    // Defaulted, not converted. Every writer coerces to an integer before the
    // document is written (createStudent and updateStudent below), so a
    // non-integer here means a document nothing in this app wrote; zero is the
    // safe reading of that, because a rate is what gets charged.
    rateMinor: Number.isInteger(raw.rateMinor) ? raw.rateMinor : 0,
    // The rule is the core's (#214), whose binding takes a string; anything
    // else — a number, an object, `true` — crosses the wasm boundary as
    // "memory access out of bounds", which names neither the field nor the
    // file (#215). Coerced here because this reads documents nothing in this
    // app wrote.
    pricingMode: coreSync().resolvePricingMode(
      typeof raw.pricingMode === 'string' ? raw.pricingMode : '',
    ).id,
    // `../domain/people`'s (#214), and not a default (#352): the field is
    // mandatory, and `studentDurationMin` answers `null` for a document without
    // one rather than inventing a thirty. Stored as `defaultDurationMin`, read
    // as `durationMin`.
    durationMin: studentDurationMin(raw.defaultDurationMin),
    // Every student belongs to a family, because the family is what gets
    // billed — but the field is genuinely nullable, not merely defaulted:
    // createStudent with `family: null` and no familyId writes one, and
    // normaliseStudent's text() stores null rather than ''. forFamily() then
    // matches on that null, so the coalesce here is what keeps a familyless
    // student out of every family's list instead of into the '' one.
    familyId: raw.familyId || null,
    // The write path's default with its `text()`, so the two agree about a
    // whitespace-only status (which the rules enum refuses).
    status: studentStatus(raw.status),
  };
}

/**
 * A stored row as this provider's documents are shaped.
 *
 * `pendingWrite` is **our** queue's on the store path, through the overlay,
 * and the SDK's mutation queue's on the snapshot path. `partial` is #268,
 * carried so a screen can ask rather than re-derive it from the ops it
 * queued, which a read cannot see.
 */
const asStudent = ({ id, data, pendingWrite, partial }) =>
  normalizeStudent({ id, ...data, pendingWrite, partial });

// ── Does this student have history? Asked of the server, not of a window ────
//
// `planStudentDeletion` in domain/people.js branches on it: a student with
// lesson or billing history is retired, one with none at all is really deleted,
// and **nothing is ever orphaned**. That promise rests on this list.
//
// ── Not the page's in-memory windows (#318) ────────────────────────────────
//
// Every provider array is a `limit()`ed window — 5,000 rows in the ledger and
// attendance providers — and **a window that hit its limit looks exactly like a
// complete one at the call site**. A studio past the bound would read as "this
// student appears nowhere", and the delete would orphan every row naming them.
// **A `limit(1)` server query cannot fail that way**: an empty result is the
// server saying there is no such row.
//
// ── `getDocsFromServer`, not `getDocs`, and offline this refuses to delete ──
//
// `getDocs` falls back to the SDK's local cache, which is a window again.
// `getDocsFromServer` rejects instead, this answers `null`, and `null` retires.
// So **a delete made offline retires everybody, including students with no
// history** — ours, since MMS has no offline editing. A wrongly retired student
// is a status the teacher flips back; a wrongly deleted one is gone.
// `StudentsPage` tells the teacher (`uncertainDeletionMessage`, #408).
//
// ── The server cannot see our queue ───────────────────────────────────────
//
// A student whose only history is a row this device has queued and the server
// has not answered for — entered seconds ago, or offline — would probe clean.
// So `probeSelection` below reads the queue first and the server second; the
// two blind spots do not overlap. `src/data/queuedHistory.js` is the queue
// half.
//
// ── What counts (#321) ─────────────────────────────────────────────────────
//
// MMS's confirm gives a *reason* — history is kept "for historical reporting" —
// and no list of collections. The reading below is ours:
//
//   - The six `studentId` collections are all records of something that
//     happened — money, attendance, a package sold, a credit granted, practice
//     logged, a piece assigned.
//   - **`eventSeries` counts, where MMS differs.** MMS hard-deletes a student
//     whose only trace is a scheduled, never-marked lesson, and leaves the
//     priced lesson naming nobody. Here such a student is retired, so the
//     orphan promise holds. Whether MMS counts a practice log or repertoire
//     piece is not known.
//   - **`tags` does not count** (#320). A tag is a label a teacher maintains,
//     not a record of anything, and a delete strips the student from every
//     tag — see `tagsHoldingAny` and `planTagDetachment` below.
//
// If a collection is added that stores a student's id, it belongs in this list.
// The orphan promise in domain/people.js is a claim about this array and
// nothing else.
export const STUDENT_REFERENCES = [
  // Ordered most-likely-first, because this short-circuits on the first hit: a
  // student with history usually costs one round trip and only a student with
  // none pays for all seven.
  { collection: COLLECTIONS.ledgerEntries, field: 'studentId', op: '==' },
  { collection: COLLECTIONS.attendance, field: 'studentId', op: '==' },
  { collection: COLLECTIONS.eventSeries, field: 'studentIds', op: 'array-contains' },
  { collection: COLLECTIONS.packageEntries, field: 'studentId', op: '==' },
  { collection: COLLECTIONS.makeupEntries, field: 'studentId', op: '==' },
  { collection: COLLECTIONS.practiceEntries, field: 'studentId', op: '==' },
  { collection: COLLECTIONS.repertoire, field: 'studentId', op: '==' },
];

// How many students are probed at once. Each is up to seven sequential round
// trips, so a selection of fifty done strictly one at a time would be a long
// wait behind a disabled button; done all at once it is 350 queries in flight.
// Eight is a middle the rules are indifferent to — `withinListBound` caps the
// `limit` of one query and says nothing about how many there are.
const PROBE_CONCURRENCY = 8;

// The tag sweep's `limit()`, the rules ceiling: `withinListBound` refuses a
// client list asking for more than 5,000, so a studio with more tags cannot
// list them on the Tags tab either, and a truncation here is unreachable for a
// studio that can use the feature (#318).
const TAG_SWEEP_LIMIT = 5000;

// Firestore's disjunction cap for `array-contains-any`. Ten is the long-standing
// floor; newer backends allow thirty, and asking for ten works against both.
const CONTAINS_ANY_MAX = 10;

// ── A reconnect is not an answer (#408) ────────────────────────────────────
//
// Concluding on a read's first rejection is right for a refused read and a
// malformed query, and wrong for **`unavailable`**, Firestore's documented
// transient, which a client one second into a reconnect rejects with. A
// `null` from it would retire a student the teacher asked to delete.
//
// A read is idempotent, so retrying risks nothing, and if the retries run out
// the answer is still `null`, which still retires: this only converts some
// wrong retirements into right deletes. After the budget the probe still says
// *I could not tell* (#318), and the plan's `uncertain` reaches the screen —
// see `deleteStudents` and `StudentsPage`. This is ours (#306, #286): MMS's
// delete is one server call, with no probe to be unsure in.
const TRANSIENT = 'unavailable';

// Attempts, and the wait between them: sized to cover a reconnect rather than
// an outage, since this runs behind a disabled Delete button and the fallback
// is safe. Four attempts 250 ms apart is under a second.
const PROBE_TRIES = 4;
const PROBE_WAIT_MS = 250;

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * One server read, settled rather than thrown: `{ snap }` or `{ err }`.
 *
 * Both handlers return a value, so this never rejects and the loops below
 * have no `try`. The two-argument form: the rejection handler is for this
 * query's own failure, and a trailing `.catch` would also swallow anything the
 * success path threw (#184).
 */
const settleRead = (q) => getDocsFromServer(q).then((snap) => ({ snap }), (err) => ({ err }));

/**
 * Ask until the answer is an answer, or the budget runs out.
 *
 * Returns the same `{ snap }` / `{ err }`, where `err` on the way out is the
 * **last** one seen, so a caller reports what actually stopped this.
 *
 * `attempts` comes out with it, because the last error does not say whether
 * this retried: `unavailable` → retry → `permission-denied` is an ordinary
 * token refresh during a reconnect.
 */
async function askWithRetry(q) {
  let last = null;
  let attempts = 0;
  for (let attempt = 1; attempt <= PROBE_TRIES; attempt += 1) {
    attempts = attempt;
    last = await settleRead(q);
    if (!last.err) return { ...last, attempts };
    // Only the transient is worth asking again. A refusal or a malformed query
    // is this collection's answer and would be the same answer four times.
    if (last.err.code !== TRANSIENT) return { ...last, attempts };
    if (attempt < PROBE_TRIES) await wait(PROBE_WAIT_MS);
  }
  return { ...last, attempts };
}

/**
 * What the log says when a read gave up, and what it will not claim.
 *
 * `consequence` is the caller's, because the two callers have different ones: a
 * history probe that cannot tell retires **one** student, and a tag sweep that
 * cannot tell retires the **whole selection**. A shared sentence would have
 * understated the second every time it printed.
 */
/**
 * @param attempts how many times `askWithRetry` actually asked. **Not derivable
 *   from `err`**, which is only the last one.
 *
 * The sentence itself is `unansweredReadMessage` in domain/people.js, so
 * `test/domain/` can assert it — this module reaches `src/lib/firebase` and no
 * pure project can load it (#292).
 */
const couldNotAsk = (what, err, consequence, attempts = 1) => unansweredReadMessage({
  what,
  code: err?.code || null,
  consequence,
  attempts,
  tries: PROBE_TRIES,
  waitMs: PROBE_WAIT_MS,
  transientCode: TRANSIENT,
});

/**
 * Has this student been referenced by anything that counts as history?
 *
 * `true` / `false` / `null`, where `null` is *cannot tell* — see
 * `planStudentDeletion`, which retires on it.
 */
async function probeStudentHistory(uid, studentId) {
  for (const ref of STUDENT_REFERENCES) {
    const { snap, err, attempts } = await askWithRetry(query(
      col(uid, ref.collection),
      where(ref.field, ref.op, studentId),
      limitTo(1),
    ));
    if (err) {
      console.error(
        couldNotAsk(
          `${ref.collection} did not answer whether ${studentId} has history`, err,
          'Treating this student as unknown, which retires them rather than deleting them.',
          attempts,
        ),
        err,
      );
      return null;
    }
    if (!snap.empty) return true;
  }
  return false;
}

/**
 * What this device has queued and the server has not answered for, or `null` if
 * the queue could not be read.
 *
 * `getDocsFromServer` cannot see a write still sitting in our queue (#318),
 * so a student whose only history is a row entered seconds ago, or offline,
 * would probe clean without this.
 *
 * `null` is "the queue did not answer", which retires, for the reason every
 * other `null` on this path does: not knowing must not delete.
 */
async function queuedHistory(uid) {
  try {
    const records = await writeQueue().records();
    return queuedStudentMentions(records, uid, STUDENT_REFERENCES.map((r) => r.collection));
  } catch (err) {
    // For `records()`' own failure and nothing else: it is the only statement
    // in the `try` that can throw for a reason of its own (#184).
    console.error(
      '[students] the write queue did not say what this device has written but not sent; '
      + 'treating every student as unknown, which retires rather than deletes:',
      err,
    );
    return null;
  }
}

/**
 * The whole selection's answers, a few students at a time.
 *
 * **The queue is consulted first, and only ever as a *yes*.** A mention there is
 * history this device wrote, so the answer is settled and seven round trips are
 * saved; its *silence* is never a no, because it is one device's outbox rather
 * than the studio's record, so everybody it does not name is still asked of the
 * server. A queue that could not be read at all settles the whole selection as
 * unknown, which retires.
 *
 * `onProgress({ checked, total })` is called as answers land (#331). It
 * reports **answers**, not queries: a student the queue settled is counted
 * when the queue is read, and one the server answered when its slice returns,
 * so the count moves in jumps of up to eight.
 *
 * It is not called for the tag sweep that follows in `deleteStudents`, which
 * is per chunk of removals rather than per student; the probes are the
 * dominant cost.
 */
async function probeSelection(uid, ids, onProgress) {
  // Announced before the first await, so a caller renders `0 of 40` rather than
  // nothing at all for the length of the queue read. A selection answered
  // entirely from the queue would otherwise show no count whatsoever.
  const total = ids.length;
  let checked = 0;
  const report = () => onProgress?.({ checked, total });
  report();

  const queued = await queuedHistory(uid);
  // Every answer at once, and it is `null` for all of them — the queue could not
  // be read, so the whole selection is unknown and retires. Reporting the total
  // here keeps the count from stopping at zero on the one path that does no
  // probing at all.
  if (queued === null) {
    checked = total;
    report();
    return new Map(ids.map((id) => [id, null]));
  }

  const answers = new Map();
  const toAsk = [];
  for (const id of ids) {
    if (queued.has(id)) answers.set(id, true);
    else toAsk.push(id);
  }
  checked = answers.size;
  report();
  for (let i = 0; i < toAsk.length; i += PROBE_CONCURRENCY) {
    const slice = toAsk.slice(i, i + PROBE_CONCURRENCY);
    const found = await Promise.all(slice.map((id) => probeStudentHistory(uid, id)));
    slice.forEach((id, n) => answers.set(id, found[n]));
    checked = answers.size;
    report();
  }
  return answers;
}

/**
 * Every tag holding any of these students, or `null` if the question could not
 * be asked.
 *
 * `null` rather than an empty list, because the two mean opposite things here:
 * "no tag holds them" lets the delete proceed, and "I could not look" must not.
 */
async function tagsHoldingAny(uid, studentIds) {
  const byId = new Map();
  for (let i = 0; i < studentIds.length; i += CONTAINS_ANY_MAX) {
    // Retried on the transient as the history probe is: a `null` from this
    // sweep re-takes the plan with *every* student unknown, so one
    // `unavailable` would retire the whole selection (#408).
    const { snap, err, attempts } = await askWithRetry(query(
      col(uid, COLLECTIONS.tags),
      where('studentIds', 'array-contains-any', studentIds.slice(i, i + CONTAINS_ANY_MAX)),
      limitTo(TAG_SWEEP_LIMIT),
    ));
    if (err) {
      console.error(
        couldNotAsk(
          'could not read the tags a delete would have to strip', err,
          'Treating EVERY student in this selection as unknown, so the whole selection retires '
          + 'rather than deleting anybody — removing a student while blind to their tag '
          + 'memberships is the orphan this path exists to prevent.',
          attempts,
        ),
        err,
      );
      return null;
    }
    // Keyed, because a tag holding two of the selected students comes back in
    // two of these pages and must be planned once.
    for (const d of snap.docs) byId.set(d.id, { id: d.id, ...d.data() });
  }
  return [...byId.values()];
}

export function StudentsProvider({ children }) {
  const { user } = useAuth();

  // `orderBy('lastName')` is the only `orderByField` a window passes, and the
  // reason `appStore`'s receipt stores delivered order: a store that handed
  // rows back in IndexedDB key order would hand them back in **id** order, and
  // nothing would throw, log, or look wrong.
  //
  // A stale read is marked, never blocked (#207 Q5): `readState` says `stale`
  // and this still hands the rows over. `loading` is #267's predicate on the
  // store path, not `state === 'loading'`, so a studio with nothing stored that
  // has queued a creation this session shows it instead of a spinner.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.students,
    limit: PAGE_LIMIT,
    orderByField: 'lastName',
    withMetadata: true,
    normalise: asStudent,
  });
  const students = read.docs;
  const loading = read.loading;

  const value = useMemo(
    () => ({
      loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,
      students,
      // Which of the two paths answered, and what the store says about the
      // server window. `readState` and `readSeenAt` are `null` on the snapshot
      // path.
      readVia: read.readVia,
      readState: read.readState,
      readSeenAt: read.readSeenAt,
      activeStudents: students.filter((s) => s.status === 'active'),
      byId: (id) => students.find((s) => s.id === id) || null,
      // The calendar views call getStudent().
      getStudent: (id) => students.find((s) => s.id === id) || null,
      forFamily: (familyId) => students.filter((s) => s.familyId === familyId),

      // Creating a student also creates its family and that family's first
      // contact, in one batch, mirroring the MMS wizard where step 2 either
      // makes a new family or joins an existing one. Batched so a student can
      // never exist without the family that pays for it, nor a new family
      // without somebody to send its invoices to — and batches commit offline,
      // so this holds with no signal.
      //
      // `contact` is ignored when joining an **existing** family, exactly as
      // `family` is: that account already has its contacts and the wizard's
      // Family step does not collect a parent for it.
      createStudent: async ({ student, family = null, contact = null }) => {
        if (!user) return null;
        // The studio's first student, judged as the getting-started checklist
        // judges it: the list has loaded and is empty (#458,
        // src/lib/analyticsEvents.js).
        const first = !loading && students.length === 0;
        const batch = writeBatch(db);
        const studentId = freeId('sdt');
        let familyId = student.familyId || null;

        if (family && !familyId) {
          familyId = freeId('fml');
          batch.set(docRef(user.uid, COLLECTIONS.families, familyId), {
            ...normaliseFamily(family),
            createdAt: serverTimestamp(),
          });
          // The parent the wizard's Family step collected, as a contact record
          // (#319). A new family has to gain a `familyContacts` row or it has
          // nobody to bill: `familyBillingContact` resolves the recipient out of
          // that collection and a family with no rows in it resolves to null.
          //
          // **In the same batch as the family**: one commit, which commits
          // offline.
          //
          // `isInvoiceRecipient: true` unconditionally: this is the account's
          // first contact and the wizard has no box for the flag. It is what
          // `scripts/migrate-family-contacts.mjs` stamps on a migrated inline
          // contact.
          if (contact) {
            batch.set(docRef(user.uid, COLLECTIONS.familyContacts, freeId('fct')), {
              ...normaliseFamilyContact({ ...contact, familyId, isInvoiceRecipient: true }),
              createdAt: serverTimestamp(),
            });
          }
        }

        // Normalised rather than spread: the stored shape is defined by
        // domain/people.js, so a stray form field cannot become a column
        // nothing reads, and email/phone mean the same thing here as on the
        // family beside it.
        batch.set(docRef(user.uid, COLLECTIONS.students, studentId), {
          ...normaliseStudent({ ...student, familyId }),
          // A guard, not the conversion. The forms parse a typed rate in the
          // studio's own currency before it gets here (useAddStudentForm), so
          // this branch only catches a caller that handed over a decimal — and
          // it has no studio to ask, so it assumes the two-place default rather
          // than guessing at an exponent. See src/domain/locale.js.
          rateMinor: Number.isInteger(student.rateMinor)
            ? student.rateMinor
            : coreSync().fromDecimal(student.rateMinor ?? 0),
          pricingMode: coreSync().resolvePricingMode(student.pricingMode).id,
          createdAt: serverTimestamp(),
        });

        await batch.commit();
        if (first) trackEvent(ANALYTICS.firstStudentAdded);
        return { studentId, familyId };
      },

      // A rename carries the students filed under the category too (#110).
      //
      // The student record denormalises `categoryLabel` as an event series
      // does, and it is rendered in the sidebar's Lesson Settings card, the
      // students list's Category column, and the report runner's `Category`
      // field.
      //
      // The rules are `relabelCategory`'s in data/useEvents.jsx: **matched on
      // `categoryId` and never on the old name**, only where the stored label
      // differs (so a save with no rename in it writes nothing), chunked at 400
      // because a batch holds 500, and idempotent — a half-finished run is
      // repaired by saving the category again.
      //
      // Bounded by what this provider holds, `PAGE_LIMIT` = 500; a studio past
      // that has students the list itself does not draw.
      relabelCategory: async ({ categoryId, label }) => {
        if (!user || !categoryId || !label) return 0;
        const targets = students
          .filter((s) => s.categoryId === categoryId && s.categoryLabel !== label)
          .map((s) => docRef(user.uid, COLLECTIONS.students, s.id));
        for (let i = 0; i < targets.length; i += 400) {
          const batch = writeBatch(db);
          for (const ref of targets.slice(i, i + 400)) {
            batch.set(ref, { categoryLabel: label, updatedAt: serverTimestamp() }, { merge: true });
          }
          await batch.commit();
        }
        return targets.length;
      },

      // Deleting students, which for most of them is not a delete (#175).
      //
      // The rule and MMS's own words are `planStudentDeletion` in
      // domain/people.js; this is the write half. A student with lesson or
      // billing history is set `inactive` and kept, so nothing that references
      // their id is ever orphaned; a student with no history at all is removed.
      // A student we cannot decide about is retired, which is the recoverable
      // mistake — see the predicate's contract at the plan function.
      //
      // **One batch, so the two halves do not half-happen**, and batches
      // commit offline. Chunked at 400 like `relabelCategory`, because a batch
      // holds 500 operations; a selection of more than 400 students can be
      // split at a chunk boundary.
      //
      // Returns the plan, so a caller can say what it did; `StudentsPage`
      // reports `uncertain` (#408).
      //
      // ── It asks the server before it writes (#318) ────────────────────────
      //
      // The caller hands in no `hasHistory` predicate (see `STUDENT_REFERENCES`
      // above); this is the only place that can guarantee the question was
      // asked before the write. Up to seven round trips per student, eight
      // students at a time, behind the confirm's `busy`.
      //
      // **`onProgress` differs from MMS (#331)**, which shows nothing while its
      // delete runs (see `DeleteStudentsDialog`): MMS's delete is one server
      // call, and this is up to seven round trips per student. It reports how
      // many of the selected students have been *decided* — not a preview of
      // what will be deleted, which cannot be known before the queries.
      //
      // ── The tag sweep, in the same batch (#320) ───────────────────────────
      //
      // `studentIds` lives on the tag, so deleting a student removes nothing
      // from any tag they were in. Every removal's memberships are read first
      // and stripped here, so tag membership does not count as history.
      //
      // **The detachments go first in the op list.** A selection big enough to
      // chunk could otherwise put the student's delete in one batch and the tag
      // write in the next, and a failure between them would leave a tag naming
      // a student that no longer exists — the one thing the orphan promise in
      // domain/people.js rules out. Ordered this way the same failure leaves a
      // surviving student missing a label, which is visible and re-tickable.
      //
      // **And if the tags cannot be read, nothing is deleted.** `tagsHoldingAny`
      // answers `null` for that, and the plan is re-taken with every student
      // unknown, so the whole selection retires. Removing a student while blind
      // to their memberships is exactly the orphan this is here to prevent.
      //
      // ── The family is not touched (#175) ──────────────────────────────────
      //
      // No family document is written, read or considered here. A family is a
      // **billing entity in its own right**: `ledgerEntries.familyId` and every
      // invoice point at it, and a payment may name no student. The history
      // probe asks about a *student id*, so a cascade would remove the
      // document a studio's money points at having asked nothing about the
      // money. A family left with no students stays visible on `FamiliesPage`;
      // `removeFamily` in src/data/useFamilies.jsx (#346) deletes one nothing
      // points at, as a separate act. What MMS does with a family whose last
      // student is deleted is not known (#286, #306).
      deleteStudents: async ({ ids = [], onProgress } = {}) => {
        if (!user || ids.length === 0) {
          return { remove: [], retire: [], uncertain: [], uncertainBecause: null };
        }
        const uid = user.uid;

        const answers = await probeSelection(uid, ids, onProgress);
        let plan = planStudentDeletion(ids, (id) => (
          answers.has(id) ? answers.get(id) : null
        ));

        // **Why the plan was unsure, where it was** (#408). `plan.uncertain`
        // says *which* students got to `retire` by not knowing; this says
        // *what* could not be asked — their history, or the tags, which is
        // about all of them. `null` when nothing was unsure.
        let uncertainBecause = plan.uncertain.length ? 'history' : null;

        let detachments = [];
        if (plan.remove.length) {
          const tags = await tagsHoldingAny(uid, plan.remove);
          if (tags === null) {
            plan = planStudentDeletion(ids, () => null);
            uncertainBecause = 'tags';
          } else detachments = planTagDetachment(tags, plan.remove);
        }

        const ops = [
          ...detachments.map((d) => ({ kind: 'detach', ...d })),
          ...plan.remove.map((id) => ({ kind: 'remove', id })),
          ...plan.retire.map((id) => ({ kind: 'retire', id })),
        ];
        for (let i = 0; i < ops.length; i += 400) {
          const batch = writeBatch(db);
          for (const op of ops.slice(i, i + 400)) {
            if (op.kind === 'detach') {
              // A merging `set` with an `arrayRemove`, the shape `saveTag` in
              // useTags.jsx uses and for the same reason: the transform is
              // computed by Firestore, so it commutes with whatever another
              // device did to the same membership and never materialises the
              // array this device last saw.
              batch.set(
                docRef(uid, COLLECTIONS.tags, op.tagId),
                tagDetachmentDocument(op.studentIds, { serverTimestamp: serverTimestamp(), arrayRemove }),
                { merge: true },
              );
              continue;
            }
            const ref = docRef(uid, COLLECTIONS.students, op.id);
            if (op.kind === 'remove') batch.delete(ref);
            // `inactive` and nothing else. MMS's confirm says the record is
            // kept "so that this information is retained for historical
            // reporting", and a flag saying *why* it went inactive would be a
            // field MMS does not have and nothing here would read.
            else batch.set(ref, { status: 'inactive', updatedAt: serverTimestamp() }, { merge: true });
          }
          await batch.commit();
        }
        return { ...plan, uncertainBecause };
      },

      updateStudent: async (id, patch) => {
        if (!user) return;
        const batch = writeBatch(db);
        const clean = { ...patch, updatedAt: serverTimestamp() };
        if ('rateMinor' in clean && !Number.isInteger(clean.rateMinor)) {
          clean.rateMinor = coreSync().fromDecimal(clean.rateMinor);
        }
        batch.set(docRef(user.uid, COLLECTIONS.students, id), clean, { merge: true });
        await batch.commit();
      },
    }),
    [students, loading, read.failed, user, read.readVia, read.readState, read.readSeenAt]
  );

  return <StudentsContext.Provider value={value}>{children}</StudentsContext.Provider>;
}

export function useStudents() {
  const ctx = useContext(StudentsContext);
  if (!ctx) throw new Error('useStudents must be used within a StudentsProvider');
  return ctx;
}
